import React, { useState, useEffect } from 'react';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Slider } from '../../ui/slider';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { getLenderRateRange, formatCurrency } from './financingCalculations';

export default function FinancingInputCard({
  lenders,
  selectedLender,
  loanAmount,
  amortizationMonths,
  amortizationPeriods,
  onLenderChange,
  onLoanAmountChange,
  onAmortizationChange,
  className = '',
}) {
  const [inputValue, setInputValue] = useState(loanAmount.toString());

  useEffect(() => {
    setInputValue(loanAmount.toString());
  }, [loanAmount]);

  const handleInputChange = (value) => {
    const cleaned = value.replace(/[^0-9]/g, '');
    setInputValue(cleaned);
    const numValue = parseInt(cleaned, 10) || 0;
    onLoanAmountChange(numValue);
  };

  const handleSliderChange = (values) => {
    const value = values[0];
    onLoanAmountChange(value);
    setInputValue(value.toString());
  };

  const formatInputValue = () => {
    if (document.activeElement && document.activeElement.id === 'loan-amount-input') {
      return inputValue;
    }
    return formatCurrency(loanAmount);
  };

  const minAmount = selectedLender ? (selectedLender.minimumLoanAmount || selectedLender.minimum_loan_amount || selectedLender.loanAmountMinimum || selectedLender.loan_amount_minimum || 50000) : 50000;
  const maxAmount = selectedLender ? (selectedLender.maximumLoanAmount || selectedLender.maximum_loan_amount || selectedLender.loanAmountMaximum || selectedLender.loan_amount_maximum || 10000000) : 10000000;
  const isOutOfRange = loanAmount < minAmount || loanAmount > maxAmount;

  return (
    <div className={`bg-succession-bg-primary border border-succession-border rounded-xl p-6 shadow-succession transition-colors duration-300 ${className}`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Lender Selector */}
        <div className="space-y-2">
          <Label htmlFor="lender-select" className="text-sm font-medium">
            Select Lender
          </Label>
          <Select
            value={selectedLender ? String(selectedLender.id) : ''}
            onValueChange={onLenderChange}
          >
            <SelectTrigger id="lender-select" className="w-full">
              <SelectValue placeholder={selectedLender ? selectedLender.name : 'Choose a lender...'} />
            </SelectTrigger>
            <SelectContent>
              {lenders.map((lender) => (
                <SelectItem key={lender.id} value={String(lender.id)} textValue={lender.name}>
                  <div className="flex items-center gap-2">
                    {lender.smallLogoUrl || lender.small_logo_url ? (
                      <img
                        src={lender.smallLogoUrl || lender.small_logo_url}
                        alt={lender.name}
                        className="w-5 h-5 rounded-full"
                      />
                    ) : null}
                    <span>{lender.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedLender && (
            <Badge variant="secondary" className="text-xs">
              {getLenderRateRange(selectedLender)}
            </Badge>
          )}
        </div>

        {/* Loan Amount */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="loan-amount-input" className="text-sm font-medium">
              Loan Amount
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-succession-text-secondary" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Adjust using slider or type directly</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <Input
            id="loan-amount-input"
            type="text"
            value={formatInputValue()}
            onChange={(e) => handleInputChange(e.target.value)}
            className={`font-mono ${isOutOfRange ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="$0"
          />

          <Slider
            value={[Math.min(Math.max(loanAmount, minAmount), maxAmount)]}
            onValueChange={handleSliderChange}
            min={minAmount}
            max={maxAmount}
            step={10000}
            className="mt-3"
            disabled={!selectedLender}
          />

          <div className="flex justify-between text-xs text-succession-text-secondary mt-1">
            <span>{formatCurrency(minAmount)}</span>
            <span>{formatCurrency(maxAmount)}</span>
          </div>

          {isOutOfRange && (
            <p className="text-xs text-red-500 mt-1">
              Amount must be between {formatCurrency(minAmount)} and {formatCurrency(maxAmount)}
            </p>
          )}
        </div>

        {/* Amortization Period */}
        <div className="space-y-2">
          <Label htmlFor="amortization-select" className="text-sm font-medium">
            Amortization Period
          </Label>
          <Select
            value={amortizationMonths.toString()}
            onValueChange={(value) => onAmortizationChange(parseInt(value, 10))}
          >
            <SelectTrigger id="amortization-select" className="w-full">
              <SelectValue placeholder={amortizationPeriods.find((p) => p.months === amortizationMonths)?.label || 'Select term'} />
            </SelectTrigger>
            <SelectContent>
              {amortizationPeriods.map((period) => (
                <SelectItem key={period.months} value={period.months.toString()} textValue={period.label}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-succession-text-secondary mt-1">
            {Math.ceil(amortizationMonths / 12)} year loan term
          </p>
        </div>
      </div>
    </div>
  );
}
